import { useEffect } from 'react'
import { Alert, Button, Form, Input, InputNumber, Slider } from 'antd'
import type { SignupPricingForm } from '../../types'
import { couponDetail, findCoupon, validateCoupon } from '../../api'
import { AUTH_PRIMARY_BUTTON_CLASS } from '../../constants'
import { useSignup } from '../SignupContext'
import {
  ADVICEPREP_ENTRY_CONNECTIONS,
  ADVICEPREP_MAX_SLIDER_CONNECTIONS,
  calculateAdvicePrepPrice,
  advicePrepCommittedMonthly,
  estimateAdvicePrepUsage,
  formatAdvicePrepCurrency,
  normalizeConnections,
  ADVICEPREP_COUPON_PRODUCT,
  advicePrepAppliedCoupon,
  advicePrepCouponCopy,
} from '@/features/adviceprep/pricing'
import './AdvicePrep.css'

function PriceTile({ label, value, caption, hero }: { label: string; value: string; caption: string; hero?: boolean }) {
  return (
    <div
      className={`rounded-[0.5rem] border px-[0.8125rem] py-[0.6875rem] text-center ${
        hero ? 'border-brand-500 bg-brand-500 text-white' : 'border-gray-200 bg-white'
      }`}
    >
      <div className={`text-[0.625rem] font-bold uppercase tracking-[0.06em] ${hero ? 'text-white/85' : 'text-gray-500'}`}>
        {label}
      </div>
      <div className={`mt-[0.25rem] font-display text-[1.375rem] font-bold leading-[1.1] ${hero ? 'text-white' : 'text-gray-900'}`}>
        {value}
      </div>
      <div className={`mt-[0.125rem] text-[0.6875rem] ${hero ? 'text-white/85' : 'text-gray-500'}`}>{caption}</div>
    </div>
  )
}

export default function AdvicePrepPricingStep() {
  const {
    pricingInfoForm,
    pricingForm,
    setPricingForm,
    advicePrepConnections,
    setAdvicePrepConnections,
    setCouponValid,
    setTrialDuration,
    advicePrepCoupon,
    setAdvicePrepCoupon,
    setStep,
    runAction,
    loading,
    hideBackButton,
    couponValid,
  } = useSignup()

  const connections = normalizeConnections(advicePrepConnections)
  const monthly = calculateAdvicePrepPrice(connections)
  const usage = estimateAdvicePrepUsage(connections)
  const perConnection = monthly / connections

  useEffect(() => {
    pricingInfoForm.setFieldsValue(pricingForm)
  }, [pricingForm, pricingInfoForm])

  const buildPricingForm = (couponCode: string): SignupPricingForm => ({
    couponCode: couponCode.trim(),
    activeUsers: '',
    customUsers: connections,
  })

  const handleNext = async () => {
    await runAction(async () => {
      const values = await pricingInfoForm.validateFields()
      const normalizedPricing = buildPricingForm(values.couponCode || '')

      if (normalizedPricing.couponCode) {
        // Not Xero. Sending that sentinel makes the server validate the code
        // against the Xero product, which either blocks valid AdvicePrep codes
        // or passes one that discounts nothing.
        const coupons = await validateCoupon({
          Code: normalizedPricing.couponCode,
          Product: ADVICEPREP_COUPON_PRODUCT,
        })
        const validCoupon = findCoupon(coupons, normalizedPricing.couponCode)
        if (!validCoupon) {
          setCouponValid(false)
          setTrialDuration(null)
          setAdvicePrepCoupon(null)
          throw new Error('You have entered an invalid Promo Code')
        }
        // An unexpanded coupon is still a good code; it just prices no discount,
        // which leaves the summary on the flat fee and Stripe to apply it later.
        const detail = couponDetail(validCoupon)
        setCouponValid(true)
        setTrialDuration(detail?.duration_in_months || null)
        setAdvicePrepCoupon(advicePrepAppliedCoupon(normalizedPricing.couponCode, detail))
      } else {
        setCouponValid(false)
        setTrialDuration(null)
        setAdvicePrepCoupon(null)
      }

      setPricingForm(normalizedPricing)
      setStep('payment')
    })
  }

  const handleBack = () => {
    setPricingForm(buildPricingForm(pricingInfoForm.getFieldValue('couponCode') || ''))
    setStep('info')
  }

  return (
    <>
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message={`AdvicePrep is a flat ${formatAdvicePrepCurrency(advicePrepCommittedMonthly())} a month covering your first ${ADVICEPREP_ENTRY_CONNECTIONS} client Xero connections. Beyond that you are billed on usage, at a rate that gets cheaper as you scale.`}
      />

      <div className="adviceprep-calc mb-[1rem] rounded-[0.625rem] border border-brand-200 bg-gradient-to-br from-brand-50 to-[#e6f7ff] px-[1.25rem] py-[1.125rem]">
        <div className="font-display text-[0.9375rem] font-semibold text-gray-900">
          How many client Xero connections do you expect?
        </div>
        <div className="mb-[0.875rem] text-[0.78125rem] text-gray-600">
          Move the slider or type a number to estimate what usage would add. It does not change what you pay
          today.
        </div>

        <div className="mb-[0.875rem] flex items-center gap-[0.875rem]">
          <span className="shrink-0 text-[0.875rem] font-medium text-gray-800">Connections</span>
          <Slider
            className="min-w-0 flex-1"
            min={1}
            max={ADVICEPREP_MAX_SLIDER_CONNECTIONS}
            value={Math.min(connections, ADVICEPREP_MAX_SLIDER_CONNECTIONS)}
            onChange={(value) => setAdvicePrepConnections(normalizeConnections(value))}
            tooltip={{ open: false }}
          />
          <InputNumber
            className="w-[6.25rem] shrink-0"
            min={1}
            max={10000}
            value={connections}
            onChange={(value) => setAdvicePrepConnections(normalizeConnections(value))}
          />
        </div>

        <div className="grid grid-cols-1 gap-[0.75rem] sm:grid-cols-3">
          <PriceTile
            label="Flat Fee"
            value={formatAdvicePrepCurrency(advicePrepCommittedMonthly())}
            caption={`covers 1-${ADVICEPREP_ENTRY_CONNECTIONS}`}
            hero
          />
          <PriceTile
            label="Per connection"
            value={formatAdvicePrepCurrency(perConnection, 2)}
            caption={`estimated at ${connections}`}
          />
          <PriceTile
            label="Total monthly"
            value={formatAdvicePrepCurrency(monthly)}
            caption={`estimated at ${connections}`}
          />
        </div>

        <p className="mt-[0.75rem] text-[0.75rem] leading-[1.55] text-gray-600">
          The flat fee is all you are charged when you subscribe.
          {usage > 0
            ? ` At ${connections} connections, usage would add about ${formatAdvicePrepCurrency(usage)} a month on top — measured from the organisations you actually connect and billed in arrears.`
            : ` Connections past ${ADVICEPREP_ENTRY_CONNECTIONS} are billed on usage, measured from the organisations you actually connect.`}
        </p>
      </div>

      <Form form={pricingInfoForm} layout="vertical" initialValues={pricingForm}>
        <Form.Item name="couponCode" label="Promo Code">
          <Input
            placeholder="Please enter a coupon code if available"
            onChange={(e) => {
              if (couponValid && e.target.value.trim() !== advicePrepCoupon?.code) {
                setCouponValid(false)
                setTrialDuration(null)
                setAdvicePrepCoupon(null)
              }
            }}
          />
        </Form.Item>
      </Form>

      {couponValid && (
        <Alert
          type="success"
          showIcon
          message="Promo code applied"
          description={
            advicePrepCouponCopy(advicePrepCoupon)?.applied ??
            'The discount is applied by Stripe when you are billed, so the amount above does not change.'
          }
        />
      )}

      <div className="signup-step-actions">
        {!hideBackButton && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        <Button
          type="primary"
          loading={loading}
          disabled={loading}
          className={AUTH_PRIMARY_BUTTON_CLASS}
          onClick={() => void handleNext()}
        >
          Next
        </Button>
      </div>
    </>
  )
}
